import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
type Tier = 'ai' | 'projection' | 'dynasty';

interface LobbySlot {
  slot: number;
  status: 'open' | 'claimed';
  owner_id?: string;
  owner_name?: string;
  team_name?: string;
  abbreviation?: string;
  uniform_primary?: string;
  uniform_secondary?: string;
  uniform_tertiary?: string;
  uniform_style?: string;
  claimed_at?: string;
  conference?: string;
}

interface LobbyState {
  id: string;
  tier: Tier;
  status: 'open' | 'firing' | 'closed';
  slots: LobbySlot[];
  created_at: string;
  fill_trigger_at?: string;
  league_id?: string;
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const TIER_LABELS: Record<Tier, string> = {
  ai: '🤖 AI League',
  projection: '📊 Projection League',
  dynasty: '🏆 Dynasty League',
};

const TIER_BUY_INS: Record<Tier, number> = {
  ai: 2500,
  projection: 10000,
  dynasty: 50000,
};

const TIER_DESCRIPTIONS: Record<Tier, string> = {
  ai: 'AI-drafted teams. 15-min priority sheet → snake draft → burst sim → playoffs.',
  projection: 'Bring your NFT players. Roster selection → lineup setup → burst sim → playoffs.',
  dynasty: 'Multi-season dynasty. Same rules as AI but spans 5 seasons.',
};

const UNIFORM_STYLES = [
  { id: 'solid', label: 'Solid', preview: '■' },
  { id: 'stripes', label: 'Stripes', preview: '▥' },
  { id: 'pinstripes', label: 'Pinstripes', preview: '▤' },
  { id: 'throwback', label: 'Throwback', preview: '▦' },
  { id: 'alternate', label: 'Alternate', preview: '▧' },
];

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'https://yawtkdwhvccveuttvffu.supabase.co/functions/v1';

// ─────────────────────────────────────────────
// Helper: Slot Card
// ─────────────────────────────────────────────
function SlotCard({
  slot,
  slotNum,
  currentUserId,
  onClaim,
  isLobbyFiring,
}: {
  slot?: LobbySlot;
  slotNum: number;
  currentUserId?: string;
  onClaim: (slotNum: number) => void;
  isLobbyFiring: boolean;
}) {
  const isClaimed = slot?.status === 'claimed';
  const isOwnSlot = slot?.owner_id === currentUserId;
  const conference = slotNum <= 6 ? 'AL East' : 'AL West';

  return (
    <div
      style={{
        background: isClaimed ? '#0f1f0f' : '#0d1117',
        border: `1px solid ${isOwnSlot ? '#22c55e' : isClaimed ? '#1a3a1a' : '#1e293b'}`,
        borderRadius: 10,
        padding: '10px 12px',
        minHeight: 90,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'border-color 0.2s',
        position: 'relative',
      }}
    >
      <div style={{ fontSize: '0.65rem', color: '#4b5563', fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
        {conference} #{slotNum}
      </div>

      {isClaimed ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: slot?.uniform_primary || '#1e40af',
                border: `2px solid ${slot?.uniform_secondary || '#fff'}`,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.55rem',
                color: slot?.uniform_secondary || '#fff',
                fontWeight: 800,
              }}
            >
              {slot?.abbreviation || '???'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#e2e8f0', lineHeight: 1.2 }}>
                {slot?.team_name || 'Unknown Team'}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{slot?.owner_name || 'Player'}</div>
            </div>
          </div>
          {isOwnSlot && (
            <div style={{ fontSize: '0.65rem', color: '#22c55e', marginTop: 4, fontWeight: 600 }}>✓ YOUR SLOT</div>
          )}
        </>
      ) : (
        <>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: '#374151', fontSize: '0.75rem', fontStyle: 'italic' }}>— Open —</div>
          </div>
          {!isLobbyFiring && (
            <button
              onClick={() => onClaim(slotNum)}
              style={{
                padding: '5px 10px',
                borderRadius: 6,
                border: 'none',
                background: '#1e40af',
                color: '#93c5fd',
                cursor: 'pointer',
                fontSize: '0.72rem',
                fontWeight: 700,
                width: '100%',
              }}
            >
              CLAIM
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Claim Modal
// ─────────────────────────────────────────────
function ClaimModal({
  slotNum,
  tier,
  onClose,
  onConfirm,
  loading,
}: {
  slotNum: number;
  tier: Tier;
  onClose: () => void;
  onConfirm: (data: {
    teamName: string;
    abbreviation: string;
    uniformPrimary: string;
    uniformSecondary: string;
    uniformTertiary: string;
    uniformStyle: string;
  }) => void;
  loading: boolean;
}) {
  const [teamName, setTeamName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [uniformPrimary, setUniformPrimary] = useState('#1e40af');
  const [uniformSecondary, setUniformSecondary] = useState('#ffffff');
  const [uniformTertiary, setUniformTertiary] = useState('#f59e0b');
  const [uniformStyle, setUniformStyle] = useState('solid');
  const conference = slotNum <= 6 ? 'AL East' : 'AL West';
  const buyIn = TIER_BUY_INS[tier];

  // Auto-generate abbreviation from team name
  useEffect(() => {
    if (teamName.length >= 2) {
      const words = teamName.trim().split(/\s+/);
      if (words.length >= 3) {
        setAbbreviation((words[0][0] + words[1][0] + words[2][0]).toUpperCase());
      } else if (words.length === 2) {
        setAbbreviation((words[0].substring(0, 2) + words[1][0]).toUpperCase());
      } else {
        setAbbreviation(teamName.substring(0, 3).toUpperCase());
      }
    }
  }, [teamName]);

  const canSubmit = teamName.trim().length >= 2 && abbreviation.trim().length >= 2 && !loading;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: 14, padding: 24, maxWidth: 440, width: '100%' }}>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#e2e8f0', marginBottom: 4 }}>
          Claim Slot #{slotNum}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 16 }}>
          {conference} · Buy-in: <strong style={{ color: '#f59e0b' }}>{buyIn.toLocaleString()} BALLS</strong>
        </div>

        {/* Team Name */}
        <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 6 }}>Team Name *</label>
        <input
          value={teamName}
          onChange={e => setTeamName(e.target.value.substring(0, 30))}
          placeholder="e.g. Portland Beavers"
          maxLength={30}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#060a12', color: '#fff', fontSize: '0.9rem', marginBottom: 12, boxSizing: 'border-box' }}
        />

        {/* Abbreviation */}
        <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 6 }}>Abbreviation (3-4 chars) *</label>
        <input
          value={abbreviation}
          onChange={e => setAbbreviation(e.target.value.toUpperCase().substring(0, 4))}
          placeholder="e.g. PDX"
          maxLength={4}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: '#060a12', color: '#fff', fontSize: '0.9rem', marginBottom: 16, boxSizing: 'border-box' }}
        />

        {/* Uniform Style */}
        <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 8 }}>Uniform Style</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {UNIFORM_STYLES.map(s => (
            <button
              key={s.id}
              onClick={() => setUniformStyle(s.id)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${uniformStyle === s.id ? '#3b82f6' : '#374151'}`,
                background: uniformStyle === s.id ? '#1e40af' : 'transparent',
                color: uniformStyle === s.id ? '#fff' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              {s.preview} {s.label}
            </button>
          ))}
        </div>

        {/* Colors */}
        <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 8 }}>Colors</label>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Primary', value: uniformPrimary, set: setUniformPrimary },
            { label: 'Secondary', value: uniformSecondary, set: setUniformSecondary },
            { label: 'Tertiary', value: uniformTertiary, set: setUniformTertiary },
          ].map(c => (
            <div key={c.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{c.label}</div>
              <input
                type="color"
                value={c.value}
                onChange={e => c.set(e.target.value)}
                style={{ width: 44, height: 36, borderRadius: 8, border: '1px solid #374151', background: 'none', cursor: 'pointer', padding: 2 }}
              />
              <div style={{ fontSize: '0.65rem', color: '#4b5563', fontFamily: 'monospace' }}>{c.value}</div>
            </div>
          ))}
          {/* Preview badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 10,
              background: uniformPrimary,
              border: `3px solid ${uniformSecondary}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: uniformSecondary,
              fontWeight: 800,
              fontSize: '0.75rem',
            }}>
              {abbreviation || '???'}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ teamName, abbreviation, uniformPrimary, uniformSecondary, uniformTertiary, uniformStyle })}
            disabled={!canSubmit}
            style={{
              flex: 2, padding: '10px', borderRadius: 8, border: 'none',
              background: canSubmit ? '#22c55e' : '#1e293b',
              color: canSubmit ? '#fff' : '#6b7280',
              cursor: canSubmit ? 'pointer' : 'default',
              fontWeight: 700, fontSize: '0.9rem',
            }}
          >
            {loading ? 'Claiming…' : `Claim for ${buyIn.toLocaleString()} BALLS`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
interface QuickLeagueLobbyPageProps {
  userData?: any;
  onNavigate?: (route: string) => void;
}

interface ActiveQuickLeague {
  league_id: string;
  league_name: string;
  league_status: string;
  current_day: number;
  team_name: string;
  team_id: string;
  wins: number;
  losses: number;
}

const QuickLeagueLobbyPage: React.FC<QuickLeagueLobbyPageProps> = ({ userData, onNavigate }) => {
  const [activeTier, setActiveTier] = useState<Tier>('ai');
  const [lobbies, setLobbies] = useState<Record<Tier, LobbyState | null>>({ ai: null, projection: null, dynasty: null });
  const [claimModal, setClaimModal] = useState<{ slotNum: number } | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeLeagues, setActiveLeagues] = useState<ActiveQuickLeague[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentUserId = userData?.id || localStorage.getItem('userId');

  const fetchActiveLeagues = useCallback(async () => {
    if (!currentUserId) return;
    try {
      // Find teams owned by this user in quick leagues
      const { data: teams } = await supabase
        .from('teams')
        .select('id, name, league_id, wins, losses')
        .eq('owner_id', currentUserId);
      
      if (teams && teams.length > 0) {
        const leagueIds = [...new Set(teams.map(t => t.league_id))];
        const { data: leagues } = await supabase
          .from('leagues')
          .select('id, name, status, current_day')
          .in('id', leagueIds)
          .eq('league_type', LEAGUE_TYPE.QUICK);
        
        if (leagues) {
          const leagueMap: Record<string, any> = {};
          for (const l of leagues) leagueMap[l.id] = l;
          
          const active: ActiveQuickLeague[] = [];
          for (const t of teams) {
            const l = leagueMap[t.league_id];
            if (l) {
              active.push({
                league_id: l.id,
                league_name: l.name,
                league_status: l.status,
                current_day: l.current_day || 1,
                team_name: t.name,
                team_id: t.id,
                wins: t.wins || 0,
                losses: t.losses || 0,
              });
            }
          }
          setActiveLeagues(active);
        }
      } else {
        setActiveLeagues([]);
      }
    } catch (e) {
      // Silently fail
    }
  }, [currentUserId]);

  const fetchLobbies = useCallback(async () => {
    try {
      // Use direct Supabase query (public read via RLS)
      const { data } = await supabase.from('ql_lobby_state').select('*');
      if (data) {
        const byTier: Record<Tier, LobbyState | null> = { ai: null, projection: null, dynasty: null };
        for (const l of data) {
          if (l.tier === 'ai' || l.tier === 'projection' || l.tier === 'dynasty') {
            byTier[l.tier as Tier] = l as LobbyState;
          }
        }
        setLobbies(byTier);
      }
    } catch (e) {
      // Silently fail on polling errors
    }
  }, []);

  useEffect(() => {
    fetchLobbies();
    fetchActiveLeagues();
    pollRef.current = setInterval(() => { fetchLobbies(); fetchActiveLeagues(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchLobbies, fetchActiveLeagues]);

  const handleClaim = async (data: {
    teamName: string;
    abbreviation: string;
    uniformPrimary: string;
    uniformSecondary: string;
    uniformTertiary: string;
    uniformStyle: string;
  }) => {
    if (!claimModal) return;
    if (!currentUserId) {
      setError('You must be signed in to claim a slot.');
      return;
    }

    setClaiming(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Get display name from userData
      const ownerName = userData?.username || userData?.display_name || currentUserId?.slice(0, 8) || 'Player';

      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/ql-lobby-fix`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          action: 'claim-slot',
          tier: activeTier,
          slot_number: claimModal.slotNum,
          user_id: currentUserId,
          owner_name: ownerName,
          team_name: data.teamName,
          abbreviation: data.abbreviation,
          uniform_primary: data.uniformPrimary,
          uniform_secondary: data.uniformSecondary,
          uniform_tertiary: data.uniformTertiary,
          uniform_style: data.uniformStyle,
        }),
      });

      const result = await res.json();
      if (!res.ok || result.error) {
        setError(result.error || 'Failed to claim slot');
      } else {
        setSuccess(`Slot #${claimModal.slotNum} claimed! ${result.league_launched ? '🚀 League is launching!' : ''}`);
        setClaimModal(null);
        await fetchLobbies();
        
        // For AI tier: auto-trigger check-fill to fill empty slots and fire league
        if (activeTier === 'ai' && !result.league_launched) {
          setSuccess('🤖 Filling AI slots… launching league!');
          const cfRes = await fetch(`${SUPABASE_FUNCTIONS_URL}/ql-lobby-fix`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ action: 'check-fill', tier: 'ai' }),
          });
          const cfResult = await cfRes.json();
          if (cfResult.league_id) {
            setSuccess(`🚀 League created! Redirecting to draft…`);
            setTimeout(() => {
              if (onNavigate) onNavigate(`/league/${cfResult.league_id}/draft-sheet`);
            }, 1500);
          }
          await fetchLobbies();
        }
      }
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally {
      setClaiming(false);
    }
  };

  const handleUnclaim = async () => {
    if (!currentUserId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const lobby = lobbies[activeTier];
      const mySlotData = lobby?.slots?.find(s => s.owner_id === currentUserId);

      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/ql-lobby-fix`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          action: 'unclaim-slot',
          tier: activeTier,
          slot_number: mySlotData?.slot,
          user_id: currentUserId,
        }),
      });

      const result = await res.json();
      if (!res.ok || result.error) {
        setError(result.error || 'Failed to unclaim');
      } else {
        setSuccess(`Slot unclaimed. Refund processed.`);
        await fetchLobbies();
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const lobby = lobbies[activeTier];
  const slots: LobbySlot[] = lobby?.slots || [];
  const claimedCount = slots.filter(s => s.status === 'claimed').length;
  const isLobbyFiring = lobby?.status === 'firing' || lobby?.status === 'closed';
  const mySlot = slots.find(s => s.owner_id === currentUserId);

  // Build full 12 slots
  const fullSlots: (LobbySlot | undefined)[] = Array.from({ length: 12 }, (_, i) => {
    const num = i + 1;
    return slots.find(s => s.slot === num) || undefined;
  });

  const eastSlots = fullSlots.slice(0, 6);
  const westSlots = fullSlots.slice(6, 12);

  return (
    <div style={{ background: '#060a12', minHeight: '100vh', color: '#fff', padding: '0 0 40px 0' }}>
      {/* Header */}
      <div style={{ background: '#0d1117', borderBottom: '1px solid #1e293b', padding: '16px 20px' }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#e2e8f0' }}>⚡ Quick Leagues</h1>
        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
          Claim a slot. Fill the lobby. Burst sim to glory.
        </p>
      </div>

      {/* Your Active Quick Leagues */}
      {activeLeagues.length > 0 && (
        <div style={{ background: '#0a0e17', borderBottom: '1px solid #1e293b', padding: '12px 20px' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>🎯 Your Active Quick Leagues</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeLeagues.map(al => {
              const statusColors: Record<string, string> = {
                drafting: '#60a5fa', free_agency: '#f59e0b', active: '#22c55e',
                midseason_pause: '#a78bfa', pre_playoff_pause: '#f97316',
                playoffs: '#ef4444', viewing: '#6b7280',
              };
              const statusLabel: Record<string, string> = {
                drafting: '📋 Drafting', free_agency: '💰 Free Agency', active: '⚾ Simming Day ' + al.current_day,
                midseason_pause: '⏸️ Midseason FA', pre_playoff_pause: '🏟️ Playoffs Soon',
                playoffs: '🏆 Playoffs', viewing: '👀 Results',
              };
              return (
                <div key={al.league_id}
                  onClick={() => {
                    if (!onNavigate) return;
                    if (al.league_status === 'drafting') {
                      onNavigate(`/league/${al.league_id}/draft-sheet`);
                    } else if (al.league_status === 'free_agency') {
                      onNavigate(`/league/${al.league_id}/free-agency`);
                    } else if (al.league_status === 'active' || al.league_status === 'midseason_pause' || al.league_status === 'pre_playoff_pause' || al.league_status === 'playoffs') {
                      onNavigate(`/ql-team/${al.league_id}/${al.team_id}`);
                    } else {
                      onNavigate(`/league/${al.league_id}`);
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: 8,
                    background: '#0d1117', border: '1px solid #1e293b', cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#e2e8f0' }}>{al.team_name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{al.league_name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: statusColors[al.league_status] || '#6b7280' }}>
                      {statusLabel[al.league_status] || al.league_status}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                      {al.wins}-{al.losses}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tier Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', background: '#0a0e17' }}>
        {(['ai', 'projection', 'dynasty'] as Tier[]).map(tier => {
          const tierLobby = lobbies[tier];
          const count = tierLobby?.slots?.filter(s => s.status === 'claimed').length || 0;
          return (
            <button
              key={tier}
              onClick={() => setActiveTier(tier)}
              style={{
                flex: 1,
                padding: '12px 8px',
                background: activeTier === tier ? '#0d1117' : 'transparent',
                color: activeTier === tier ? '#60a5fa' : '#6b7280',
                border: 'none',
                borderBottom: activeTier === tier ? '2px solid #3b82f6' : '2px solid transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.8rem',
                transition: 'all 0.15s',
              }}
            >
              <div>{TIER_LABELS[tier]}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 400, marginTop: 2 }}>
                {TIER_BUY_INS[tier].toLocaleString()} BALLS · {count}/12
              </div>
            </button>
          );
        })}
      </div>

      {/* Lobby Content */}
      <div style={{ padding: '16px 14px', maxWidth: 700, margin: '0 auto' }}>
        {/* Tier description */}
        <div style={{ background: '#0a0e17', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem', color: '#9ca3af' }}>
          {TIER_DESCRIPTIONS[activeTier]}
        </div>

        {/* Status banner */}
        {isLobbyFiring ? (
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '12px 16px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontWeight: 800, color: '#fca5a5', fontSize: '1rem' }}>🚀 LOBBY FIRING</div>
            <div style={{ color: '#f87171', fontSize: '0.8rem', marginTop: 4 }}>League is being created… refresh to join the draft!</div>
          </div>
        ) : (
          <div style={{ background: '#0a1520', border: '1px solid #1e40af', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#60a5fa' }}>⏳ {claimedCount}/12 slots claimed</div>
              {lobby?.fill_trigger_at && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                  Auto-fires or dissolves {new Date(lobby.fill_trigger_at).toLocaleString()}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {mySlot && (
                <button
                  onClick={handleUnclaim}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #6b7280', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  Leave Lobby
                </button>
              )}
              {!mySlot && (
                <button
                  onClick={() => setClaimModal({ slotNum: (slots.find(s => s.status !== 'claimed')?.slot ?? fullSlots.findIndex(s => !s) + 1) || 1 })}
                  style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#22c55e', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}
                >
                  Claim Slot — {TIER_BUY_INS[activeTier].toLocaleString()} BALLS
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error / Success */}
        {error && (
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#fca5a5', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>{error}</span>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontWeight: 700 }}>×</button>
          </div>
        )}
        {success && (
          <div style={{ background: '#14532d', border: '1px solid #22c55e', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#86efac', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', color: '#4ade80', cursor: 'pointer', fontWeight: 700 }}>×</button>
          </div>
        )}

        {/* Slot Grid: 2 columns (AL East | AL West) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* AL East Header */}
          <div style={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textAlign: 'center', padding: '4px 0' }}>
            AL EAST
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textAlign: 'center', padding: '4px 0' }}>
            AL WEST
          </div>

          {/* Slots */}
          {Array.from({ length: 6 }, (_, i) => {
            const eastSlot = eastSlots[i];
            const westSlot = westSlots[i];
            return (
              <React.Fragment key={i}>
                <SlotCard
                  slot={eastSlot}
                  slotNum={i + 1}
                  currentUserId={currentUserId}
                  onClaim={(num) => setClaimModal({ slotNum: num })}
                  isLobbyFiring={isLobbyFiring}
                />
                <SlotCard
                  slot={westSlot}
                  slotNum={i + 7}
                  currentUserId={currentUserId}
                  onClaim={(num) => setClaimModal({ slotNum: num })}
                  isLobbyFiring={isLobbyFiring}
                />
              </React.Fragment>
            );
          })}
        </div>

        {/* Payout Structure */}
        <div style={{ marginTop: 24, background: '#0a0e17', border: '1px solid #1e293b', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 10, fontSize: '0.9rem' }}>💰 Prize Pool</div>
          <div style={{ fontSize: '0.82rem', color: '#9ca3af', marginBottom: 8 }}>
            Total: <strong style={{ color: '#f59e0b' }}>{(TIER_BUY_INS[activeTier] * 12).toLocaleString()} BALLS</strong>
          </div>
          {[
            { label: '🥇 Champion', pct: '50%' },
            { label: '🥈 Runner-Up', pct: '25%' },
            { label: '🥉 Conf Finalists (×2)', pct: '10% each' },
            { label: '🏛️ Platform', pct: '5%' },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #111827' }}>
              <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{row.label}</span>
              <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.8rem' }}>{row.pct}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Claim Modal */}
      {claimModal && (
        <ClaimModal
          slotNum={claimModal.slotNum}
          tier={activeTier}
          onClose={() => setClaimModal(null)}
          onConfirm={handleClaim}
          loading={claiming}
        />
      )}
    </div>
  );
};

export default QuickLeagueLobbyPage;
